import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import { useNavigationUtils } from "@/utils/navigationHooks";

interface DifferentAccountDialogProps {
  open: boolean;
  onClose: () => void;
  onSwitchAccount?: () => Promise<void>;
  connectedAddress: string;
  metaMaskAddress: string; // Will be used as externalWalletAddress for compatibility
  isAttemptingSwitch?: boolean;
  walletName?: string;
}

export default function DifferentAccountDialog({
  open,
  onClose,
  connectedAddress,
  metaMaskAddress,
  walletName = "External Wallet",
}: DifferentAccountDialogProps) {
  const { t } = useTranslation();
  const [isLoading, _setIsLoading] = React.useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Reset exit animation state when dialog opens
  useEffect(() => {
    if (open) {
      setIsExiting(false);
    }
  }, [open]);

  const handleClose = () => {
    if (
      metaMaskAddress &&
      connectedAddress &&
      metaMaskAddress.toLowerCase() === connectedAddress.toLowerCase()
    ) {
      setIsExiting(true);
      setTimeout(() => {
        onClose();
      }, 300);
    } else {
      console.log("Cannot close dialog - accounts don't match");
    }
  };

  const formatAddress = (address: string) => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const { handleLogout } = useNavigationUtils();

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent dialog from closing when clicking outside or pressing escape
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "16px",
          padding: "16px",
          bgcolor: "rgba(0, 0, 0, 0.8)",
          boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.2)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          transition: "all 300ms ease-in-out",
        },
        "& .MuiBackdrop-root": {
          backdropFilter: isExiting ? "blur(0px)" : "blur(4px)",
          transition: "backdrop-filter 300ms ease-in-out",
        },
      }}
      className={`animate-jump-in animate-once animate-duration-300 animate-delay-150 animate-ease-linear ${
        isExiting
          ? "animate-jump-out animate-once animate-duration-300 animate-delay-[120ms]"
          : ""
      }`}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          pb: 0,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "warning.main" }}
        >
          {t("dialog.different_account.title", "Account Mismatch Detected")}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Box
          sx={{
            my: 2,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              mb: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "rgba(255, 193, 7, 0.1)",
              transition: "transform 300ms ease",
              transform: isExiting ? "scale(0.9)" : "scale(1)",
            }}
            className="animate-jump-in animate-once animate-duration-300 animate-delay-150 animate-ease-linear"
          >
            <ReportProblemRoundedIcon color="warning" sx={{ fontSize: 46 }} />
          </Box>
          <Typography
            variant="body1"
            gutterBottom
            sx={{
              fontWeight: 500,
              px: 2,
              py: 1,
              bgcolor: "rgba(255, 193, 7, 0.08)",
              borderRadius: "8px",
              mb: 3,
            }}
          >
            {t(
              "dialog.different_account.message",
              `You are currently logged into the application with one account, but your ${walletName || "external wallet"} is using a different account.`
            )}
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              width: "100%",
              bgcolor: "rgba(255, 255, 255, 0.05)",
              borderRadius: "12px",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              transition: "opacity 300ms ease-in-out",
              opacity: isExiting ? 0.7 : 1,
            }}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t(
                "dialog.different_account.account_info",
                "Account Information"
              )}
            </Typography>
            <Box
              sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  borderRadius: "8px",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.05)",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "dialog.different_account.connected_account",
                    "Connected Account:"
                  )}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatAddress(connectedAddress)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  borderRadius: "8px",
                  bgcolor: "rgba(255, 193, 7, 0.08)",
                  border: "1px solid rgba(255, 193, 7, 0.15)",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "dialog.different_account.current_account",
                    `${walletName || "Wallet"} Account:`
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="warning.main"
                >
                  {formatAddress(metaMaskAddress)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            mt: 2,
            px: 2,
            py: 1.5,
            borderRadius: "8px",
            bgcolor: "rgba(255, 255, 255, 0.05)",
          }}
        >
          {t(
            "dialog.different_account.instruction",
            `To continue using the application, you need to either switch your ${walletName || "wallet"} account or use the button below to request your wallet to switch to your connected account.`
          )}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "center",
          padding: "16px",
          gap: 2,
          mt: 1,
        }}
      >
        <Button
          onClick={handleLogout}
          color="warning"
          variant="contained"
          disabled={isLoading}
          sx={{
            borderRadius: "8px",
            px: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            fontSize: "12px",
            textTransform: "capitalize",
          }}
        >
          {isLoading
            ? t("common.loading", "Loading...")
            : t(
                "dialog.different_account.logout_button",
                "Logout and Switch Account"
              )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
